import { FC, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  FlowerStatDateType,
  FlowerStatItem,
} from '@shared/api/flowerStat/flowerStat.interface';
import { getSingleTeamNameLabels } from '@shared/api/flowerStat/flowerStat.util';
import CheckboxFilter, { CheckBoxFilter } from '@shared/components/CheckboxFilter';
import { DatePicker } from '@shared/containers/DatePicker/DatePicker';
import { customThemeAtom } from '@shared/state/atom/theme.atom';
import { pickWorkerName } from '@shared/utils/agencyWorkerUtils';
import { getTodayFrom, getTodayTo } from '@shared/utils/dateUtils';
import { formatDate } from '@shared/utils/formatDate';
import { mainBgColorGenerator } from '@shared/utils/mainColorGenerator';
import { Radio, Table, TableColumnsType, message } from 'antd';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';

const MAX_RANGE_DAYS = 60;

import WorkerDetailModalOpener from '../WorkerDetailModalOpener/WorkerDetailModalOpener';
import AgencyAdminShopOrderStatusBar from './AgencyAdminShopOrderStatusBar';

interface IProps {
  flowerStatList: FlowerStatItem[];
  teamTypesFilter: CheckBoxFilter | null;
  searchValue: string;
  dateType?: FlowerStatDateType;
  onDateTypeChange: (dateType: FlowerStatDateType) => void;
  onDateRangeChange: (newStart: string, newEnd: string) => void;
}

type ShopOrderTableData = {
  id: string;
  workerName: string;
  workerId: string;
  teamName: string;
  shopItemName: string;
  createdAt: string;
  paymentAt: string;
  priceRetail: number;
};

const AgencyAdminShopOrderListTableMobile: FC<IProps> = ({
  flowerStatList,
  teamTypesFilter,
  searchValue,
  dateType,
  onDateTypeChange,
  onDateRangeChange: onDateRangeChangeProp,
}: IProps) => {
  const customTheme = useRecoilValue(customThemeAtom);
  const location = useLocation();
  const showSettings = new URLSearchParams(location.search).get('settings') === 'true';

  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  // TeamType Filtering
  const [selectedTeamTypes, setSelectedTeamTypes] = useState(teamTypesFilter);

  //* 소속(teamType)이 한 곳뿐이면 분류명 대신 그 소속 이름을 칩 라벨로 표시
  const teamTypeLabels = useMemo(
    () => getSingleTeamNameLabels(flowerStatList, selectedTeamTypes),
    [flowerStatList, selectedTeamTypes],
  );

  const teamTypesFilteredList = useMemo(() => {
    if (selectedTeamTypes) {
      return flowerStatList.filter((item) => selectedTeamTypes[item.teamType ?? '']);
    } else {
      return flowerStatList;
    }
  }, [flowerStatList, selectedTeamTypes]);

  // Search
  const resultValue = useMemo(() => {
    const searcher = new FuzzySearch(teamTypesFilteredList, [
      'workerName',
      'workerNickName',
      'teamName',
      'teamType',
      'region',
      'shopItemName',
      'funeralHomeName',
      'deceasedName',
      'senderPhrase',
    ]);
    return searcher.search(searchValue);
  }, [teamTypesFilteredList, searchValue]);

  const tableData: ShopOrderTableData[] = useMemo(() => {
    return _.map(resultValue, (item): ShopOrderTableData => {
      return {
        id: item._id,
        shopItemName: item.shopItemName,
        workerName: pickWorkerName(item) ?? '',
        workerId: item.workerId,
        teamName: item.teamName,
        priceRetail: item.priceRetail,
        createdAt: item.createdAt,
        paymentAt: item.purchasedAt ?? '',
      };
    });
  }, [resultValue]);

  const columns: TableColumnsType<ShopOrderTableData> = [
    {
      title: <div className="break-keep">주문일</div>,
      dataIndex: 'paymentAt',
      key: 'paymentAt',
      align: 'center',
      render: (value: string) => {
        return (
          <p className="text-xs text-gray-600">
            {value
              ? formatDate(new Date(value), {
                  contains: { year: false, date: true },
                  dateSeparater: '/',
                })
              : ''}
          </p>
        );
      },
    },
    {
      width: '15%',
      title: '소속',
      dataIndex: 'teamName',
      key: 'teamType',
      align: 'center',
      render: (teamName: string) => {
        return <p className="break-keep text-xs">{teamName}</p>;
      },
    },
    {
      width: '15%',
      title: '지도사',
      key: 'worker',
      align: 'center',
      render: (_, record) => {
        return (
          <WorkerDetailModalOpener
            workerName={record.workerName}
            workerId={record.workerId}
          />
        );
      },
    },
    {
      title: '상품명',
      dataIndex: 'shopItemName',
      key: 'name',
      align: 'center',
      render: (name) => {
        return <p className="break-keep text-xs font-medium">{name}</p>;
      },
    },

    {
      title: <div className="break-keep">결제금액</div>,
      dataIndex: 'priceRetail',
      key: 'priceRetail',
      align: 'center',
      render: (value: number) => {
        return (
          <p className="break-keep text-xs text-gray-600">
            {(value / 10000).toFixed(1).toLocaleString()}만원
          </p>
        );
      },
    },
  ];

  const revertDates = () => {
    // 이전 값으로 강제 리렌더링하여 DatePicker UI 복원
    setDateFrom((prev) => (prev ? new Date(prev.getTime()) : null));
    setDateTo((prev) => (prev ? new Date(prev.getTime()) : null));
  };

  const validateDateRange = (from: Date, to: Date): boolean => {
    const diffDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > MAX_RANGE_DAYS) {
      message.warning(
        `조회 가능한 최대 기간은 ${MAX_RANGE_DAYS}일입니다. 기간을 다시 설정해주세요.`,
      );
      revertDates();
      return false;
    }
    return true;
  };

  //* 양쪽 날짜가 모두 설정되면 부모에게 알려 API 재호출
  const notifyDateRange = (from: Date, to: Date) => {
    onDateRangeChangeProp(from.toISOString(), to.toISOString());
  };

  const onDateFromChange = (date: Date | null) => {
    if (!date) {
      setDateFrom(null);
      return;
    }

    // 설정된 시작일이 종료일보다 클 경우 switch.
    if (dateTo && dateTo.getTime() < date.getTime()) {
      const effectiveFrom = getTodayFrom(dateTo);
      const effectiveTo = getTodayTo(date);
      if (!validateDateRange(effectiveFrom, effectiveTo)) return;
      setDateFrom(effectiveFrom);
      setDateTo(effectiveTo);
      notifyDateRange(effectiveFrom, effectiveTo);
      return;
    }

    const newFrom = getTodayFrom(date);
    if (dateTo && !validateDateRange(newFrom, dateTo)) return;
    setDateFrom(newFrom);
    if (dateTo) notifyDateRange(newFrom, dateTo);
  };

  const onDateFromTo = (date: Date | null) => {
    if (!date) {
      setDateTo(null);
      return;
    }

    // 설정된 종료일이 시작일보다 작을 경우 switch.
    if (dateFrom && dateFrom.getTime() > date.getTime()) {
      const effectiveFrom = getTodayFrom(date);
      const effectiveTo = getTodayTo(dateFrom);
      if (!validateDateRange(effectiveFrom, effectiveTo)) return;
      setDateTo(effectiveTo);
      setDateFrom(effectiveFrom);
      notifyDateRange(effectiveFrom, effectiveTo);
      return;
    }

    const newTo = getTodayTo(date);
    if (dateFrom && !validateDateRange(dateFrom, newTo)) return;
    setDateTo(newTo);
    if (dateFrom) notifyDateRange(dateFrom, newTo);
  };

  return (
    <div className="space-y-2 px-2">
      {/* 날짜 기준 선택 (settings=true 일 때만 표시) */}
      {showSettings && (
        <div className="flex justify-center">
          <Radio.Group
            value={dateType}
            onChange={(e) => onDateTypeChange(e.target.value)}
            size="small"
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="purchasedAt">판매일 기준</Radio.Button>
            <Radio.Button value="coffinOutDate">발인일 기준</Radio.Button>
          </Radio.Group>
        </div>
      )}
      {/* Date Range Picker */}
      <div className="flex items-center space-x-2 bg-opacity-60">
        {/* 시작일 */}
        <DatePicker
          placeholder="시작일"
          value={dateFrom}
          onChange={onDateFromChange}
          className="flex-1"
        />
        <div>~</div>
        {/* 종료일 */}
        <DatePicker
          placeholder="종료일"
          value={dateTo}
          onChange={onDateFromTo}
          className="flex-1"
        />
      </div>
      {/* 소속 필터 */}
      {selectedTeamTypes && (
        <div className="flex items-center border text-xs theme-bg-1 theme-border-1">
          <div
            className={`center-box self-stretch bg-opacity-40 px-2 font-bold ${mainBgColorGenerator(
              customTheme,
            )}`}
          >
            소속
          </div>
          <div className="flex flex-1 items-center justify-around">
            <CheckboxFilter
              filter={selectedTeamTypes}
              setFilter={(checked) => setSelectedTeamTypes(checked)}
              labels={teamTypeLabels}
            />
          </div>
        </div>
      )}
      <AgencyAdminShopOrderStatusBar shopOrderList={resultValue} />
      <Table
        className="w-full"
        columns={columns}
        dataSource={_.flatMapDeep([tableData])}
        pagination={{ position: ['bottomCenter'], pageSize: 15 }}
        rowKey={'id'}
        size={'small'}
        onHeaderRow={() => ({
          className: `text-xs font-bold bg-opacity-40 ${mainBgColorGenerator(
            customTheme,
          )}`,
        })}
      />
    </div>
  );
};

export default AgencyAdminShopOrderListTableMobile;
